/**
 * CR-6322: Call-off ASN delivery-lines grid.
 *
 * Renders the delivery lines using the customer's saved column layout
 * (`columnFormat`) — the same layout that drives the printed delivery note, so
 * the grid and PDF always agree. A customer admin gets an "Edit columns" button
 * that opens the picker; non-admins just see the configured layout.
 */
import { Settings2, Paperclip } from 'lucide-react';
import type { ASNColumnFormatColumn, ASNDeliveryLineDetail } from '../../lib/api';
import { asnCellText, asnColumnTotal, ASN_TOTALLED_COLUMNS } from './asn-columns';

interface ASNDeliveryLinesGridProps {
  lines: ASNDeliveryLineDetail[];
  columnFormat?: ASNColumnFormatColumn[] | null;
  formatDate: (d?: string) => string;
  onOpenAttachments: (line: ASNDeliveryLineDetail) => void;
  /** Customer admins only — shows the "Edit columns" button. */
  canEditColumns: boolean;
  onEditColumns: () => void;
}

const alignClass = (align: string): string =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

export function ASNDeliveryLinesGrid({
  lines, columnFormat, formatDate, onOpenAttachments, canEditColumns, onEditColumns,
}: ASNDeliveryLinesGridProps) {
  const visibleCols = (columnFormat || [])
    .filter((c) => c.is_visible)
    .sort((a, b) => a.sort_order - b.sort_order);

  // Whether any visible column carries a footer total (pallets / weights).
  const hasTotals = visibleCols.some((c) => ASN_TOTALLED_COLUMNS.has(c.column_id));

  if (visibleCols.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg bg-muted/10">
        No columns configured for this delivery note.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {canEditColumns && (
        <div className="flex justify-end">
          <button
            onClick={onEditColumns}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-lg hover:bg-muted/50 transition-colors"
            title="Configure which columns appear on this customer's delivery note (grid + print)"
          >
            <Settings2 className="h-3.5 w-3.5" />
            Edit columns
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              {visibleCols.map((col) => (
                <th
                  key={col.column_id}
                  className={`py-2 px-3 font-medium whitespace-nowrap ${alignClass(col.align)}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id} className="border-b hover:bg-muted/30">
                {visibleCols.map((col) => {
                  if (col.column_id === 'attachments') {
                    return (
                      <td key={col.column_id} className="py-2 px-3 text-center">
                        <button
                          onClick={() => onOpenAttachments(line)}
                          title="Attachments"
                          className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors text-blue-600 inline-flex"
                        >
                          <Paperclip className="h-4 w-4" />
                        </button>
                      </td>
                    );
                  }
                  const text = asnCellText(col.column_id, line, formatDate);
                  const isDesc = col.column_id === 'description';
                  return (
                    <td
                      key={col.column_id}
                      className={`py-2 px-3 text-xs ${alignClass(col.align)} ${isDesc ? 'max-w-[220px] truncate' : ''}`}
                      title={isDesc ? line.part_description : undefined}
                    >
                      {text}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {hasTotals && (
            <tfoot>
              <tr className="border-t-2 font-semibold bg-muted/20">
                {visibleCols.map((col, idx) => {
                  if (idx === 0) {
                    return (
                      <td key={col.column_id} className="py-2 px-3 text-xs">Total</td>
                    );
                  }
                  const total = ASN_TOTALLED_COLUMNS.has(col.column_id)
                    ? asnColumnTotal(col.column_id, lines)
                    : '';
                  return (
                    <td key={col.column_id} className={`py-2 px-3 text-xs ${alignClass(col.align)}`}>
                      {total}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
